import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Mail, ClipboardList, CheckCircle2, Eye, Layers,
  Sparkles, Camera, MessageSquare, DollarSign, MapPin,
  Heart, ChevronDown, ChevronUp, Loader2,
  Plus, Trash2, ExternalLink, Image as ImageIcon,
  Tag, Lock, GraduationCap, ShoppingBag
} from "lucide-react";

const STAGE_META = [
  { key: "envelope", title: "Envelope / Welcome", icon: Mail, description: "First touch when a client opens your invite or RSVP link." },
  { key: "intake", title: "Intake (Preferences, Inspo, Questions)", icon: ClipboardList, description: "What you collect from the client during booking. Pulled from admin category master." },
  { key: "confirmation", title: "Confirmation (Pre / During / Post)", icon: CheckCircle2, description: "What clients see before, during, and after the appointment." },
];

// =============== ENVELOPE STAGE ===============
function EnvelopeStage({ event, settings, onToggle, onJumpToBrand, saving }) {
  const brandLogo = event?.host_logo_url || event?.organization?.logo_url;
  const brandCover = event?.cover_image_url || event?.hero_image_url;
  const brandTagline = event?.tagline || event?.subtitle;
  const brandIntro = event?.host_intro || event?.welcome_message;
  return (
    <CardContent className="pt-0 pb-4 border-t">
      <div className="pt-4 space-y-4">
        <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
          <ImageIcon className="w-4 h-4 text-purple-700 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-purple-900 mb-1">Pulled from your Brand tab</p>
            <p className="text-[11px] text-purple-700 mb-3">Envelope visuals (logo, cover, tagline, hero photo) are managed in <strong>Brand</strong>. Toggle below to show / hide each on the public envelope.</p>
            <Button size="sm" variant="outline" onClick={onJumpToBrand} className="border-purple-300 text-purple-700 hover:bg-purple-100" data-testid="jump-to-brand-btn">
              <ExternalLink className="w-3 h-3 mr-1.5" /> Edit in Brand tab
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg overflow-hidden border bg-white">
            <div className="aspect-video bg-gray-100 flex items-center justify-center">
              {brandCover ? <img src={brandCover} alt="Cover" className="w-full h-full object-cover" /> : <span className="text-[10px] text-gray-400">No cover image set</span>}
            </div>
            <p className="text-[10px] text-gray-500 px-2 py-1.5 border-t">Hero / cover photo</p>
          </div>
          <div className="rounded-lg overflow-hidden border bg-white">
            <div className="aspect-video bg-gray-50 flex items-center justify-center">
              {brandLogo ? <img src={brandLogo} alt="Logo" className="max-h-12 max-w-[80%] object-contain" /> : <span className="text-[10px] text-gray-400">No logo set</span>}
            </div>
            <p className="text-[10px] text-gray-500 px-2 py-1.5 border-t">Host logo</p>
          </div>
        </div>
        <div className="space-y-1.5 p-3 rounded-lg bg-gray-50">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">Tagline</p>
          <p className="text-sm text-gray-800">{brandTagline || <span className="text-gray-400 italic">Not set in Brand tab</span>}</p>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium pt-2">Host intro</p>
          <p className="text-xs text-gray-700">{brandIntro || <span className="text-gray-400 italic">Not set in Brand tab</span>}</p>
        </div>
        <div className="space-y-3 pt-2 border-t">
          <p className="text-xs font-medium text-gray-600">Show on the public envelope:</p>
          {[
            { key: "show_logo", label: "Show host logo", icon: Sparkles },
            { key: "show_tagline", label: "Show tagline", icon: MessageSquare },
            { key: "show_hero_photo", label: "Show hero photo / cover", icon: Camera },
            { key: "show_host_intro", label: "Show host intro paragraph", icon: Heart },
          ].map((tog) => {
            const Icon = tog.icon;
            const fp = `envelope_${tog.key}`;
            const value = settings[fp] !== undefined ? !!settings[fp] : true;
            return (
              <div key={tog.key} className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Icon className="w-3.5 h-3.5 text-gray-400" /><span className="text-sm text-gray-700">{tog.label}</span></div>
                <div className="flex items-center gap-2">
                  {saving === fp && <Loader2 className="w-3 h-3 animate-spin text-purple-500" />}
                  <Switch checked={value} onCheckedChange={(v) => onToggle("envelope", tog.key, v)} data-testid={`toggle-${fp}`} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </CardContent>
  );
}

// =============== INTAKE STAGE (admin-driven, no duplication) ===============
function IntakeStage({ API, eventId, bundle, settings, onToggle, saving, onOpenMedia, onOpenServices, refresh }) {
  const [hostQuestions, setHostQuestions] = useState([]);
  const [loadingHostQ, setLoadingHostQ] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");
  const [newType, setNewType] = useState("text");

  const reload = useCallback(async () => {
    if (!bundle?.category_id || !eventId) { setLoadingHostQ(false); return; }
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/client-intake-questions?category_id=${encodeURIComponent(bundle.category_id)}&event_id=${encodeURIComponent(eventId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) {
        const d = await r.json();
        setHostQuestions(d.host_questions || []);
      }
    } catch {} finally { setLoadingHostQ(false); }
  }, [API, bundle?.category_id, eventId]);

  useEffect(() => { reload(); }, [reload]);

  const addQ = async () => {
    if (!newQuestion.trim()) { toast.error("Enter a question"); return; }
    setAdding(true);
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/client-intake-questions`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ category_id: bundle.category_id, event_id: eventId, question_text: newQuestion.trim(), type: newType })
      });
      if (r.ok) { toast.success("Question added"); setNewQuestion(""); reload(); }
      else toast.error("Could not add");
    } catch { toast.error("Network error"); } finally { setAdding(false); }
  };

  const removeQ = async (qid) => {
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/client-intake-questions/${qid}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { toast.success("Removed"); reload(); }
    } catch {}
  };

  if (!bundle) return <CardContent className="py-6 text-xs text-center text-gray-400"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></CardContent>;

  const sectionToggle = (key, label, icon, count, manageLabel, onManage) => {
    const Icon = icon;
    const fp = `intake_section_${key}`;
    const value = settings[fp] !== undefined ? !!settings[fp] : true;
    return (
      <div className="rounded-lg border border-gray-100 p-3 bg-white" data-testid={`intake-section-${key}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <div className="w-7 h-7 rounded bg-purple-50 flex items-center justify-center flex-shrink-0">
              <Icon className="w-3.5 h-3.5 text-purple-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">{label}</p>
              <p className="text-[11px] text-gray-500">{count} item{count === 1 ? "" : "s"} · admin-managed</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {saving === fp && <Loader2 className="w-3 h-3 animate-spin text-purple-500" />}
            <Switch checked={value} onCheckedChange={(v) => onToggle("intake", `section_${key}`, v)} data-testid={`toggle-${fp}`} />
          </div>
        </div>
        {onManage && (
          <Button variant="ghost" size="sm" className="mt-2 h-7 text-[11px] text-purple-700 hover:bg-purple-50 px-2" onClick={onManage} data-testid={`intake-manage-${key}`}>
            <ExternalLink className="w-3 h-3 mr-1" /> {manageLabel}
          </Button>
        )}
      </div>
    );
  };

  return (
    <CardContent className="pt-0 pb-4 border-t">
      <div className="pt-4 space-y-4">
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2.5 flex items-start gap-2">
          <Lock className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>Sensitivities, allergies, skin type and other clinical info live in <strong>Preferences</strong> below - already collected. Don't re-add as questions.</span>
        </div>

        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Auto-collected sections</p>
          
          {/* Preferences section (options from category_master.preference_schema) */}
          {sectionToggle(
            "preferences",
            `Preference options (${(bundle.preference_schema || []).map(b => b.block_label).join(" · ") || "none"})`,
            Sparkles,
            (bundle.preference_schema || []).reduce((n, b) => n + (b.fields?.length || 0), 0),
            "Manage in Admin Category Master",
            null
          )}

          {/* Inspo folder (admin-managed) */}
          {sectionToggle(
            "inspo_public",
            `Public inspo folder${(bundle.inspo_folders || [])[0]?.label ? `: ${(bundle.inspo_folders || [])[0].label}` : ""}`,
            Camera,
            (bundle.inspo_folders || []).length,
            "Manage in Media Library",
            onOpenMedia
          )}

          {/* Master products picker (e.g. "Lash for this look") */}
          {sectionToggle(
            "master_products",
            `Master Products picker (e.g. lash for the look)`,
            Tag,
            (bundle.master_products || []).length,
            "Manage in Admin Category Master",
            null
          )}

          {/* Host upsell products */}
          {sectionToggle(
            "host_upsells",
            `Your upsell products (optional add-ons)`,
            ShoppingBag,
            (bundle.host_upsell_products || []).length,
            "Manage upsells in Services",
            onOpenServices
          )}

          {/* Tier-2 host private inspo */}
          {sectionToggle(
            "host_private_inspo",
            `Your private inspo gallery (Acuity-bound)`,
            Lock,
            bundle.host_private_inspo_count || 0,
            "Manage in Media Library",
            onOpenMedia
          )}

          {/* Instructor section (conditional, only if category supports it) */}
          {bundle.instructor_mode && sectionToggle(
            "instructor_prefs",
            `Instructor preferences (only when client picks a class)`,
            GraduationCap,
            1,
            null,
            null
          )}
        </div>

        {/* Host custom questions */}
        <div className="border-t pt-3 space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Your custom questions</p>
          {loadingHostQ ? <Loader2 className="w-3 h-3 animate-spin text-purple-500 mx-auto" /> : (
            <>
              {hostQuestions.length === 0 ? (
                <p className="text-[11px] text-gray-400 italic">None yet. Add unique questions below - sensitivities/allergies/etc are NOT here, those are in preferences.</p>
              ) : (
                <div className="space-y-1.5">
                  {hostQuestions.map((q) => (
                    <div key={q.question_id} className="flex items-start gap-2 p-2 rounded border border-purple-100 bg-purple-50/40" data-testid={`host-q-${q.question_id}`}>
                      <Sparkles className="w-3 h-3 text-purple-500 mt-1 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800">{q.question_text}</p>
                        <Badge variant="secondary" className="text-[10px] capitalize mt-1">{q.type}</Badge>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => removeQ(q.question_id)} data-testid={`remove-host-q-${q.question_id}`}>
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2 flex-wrap pt-2">
                <Input value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)} placeholder="e.g. What's the venue lighting like?" className="flex-1 min-w-[180px] text-sm" data-testid="intake-add-q-input" />
                <select value={newType} onChange={(e) => setNewType(e.target.value)} className="text-xs border rounded px-2 py-1" data-testid="intake-add-q-type">
                  <option value="text">Text</option>
                  <option value="single">Single choice</option>
                  <option value="multi">Multi choice</option>
                  <option value="number">Number</option>
                </select>
                <Button size="sm" onClick={addQ} disabled={adding} data-testid="intake-add-q-btn">
                  {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />} Add
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </CardContent>
  );
}

// =============== CONFIRMATION STAGE ===============
function ConfirmationStage({ API, eventId, bundle, settings, onToggle, saving }) {
  const [hostBlocks, setHostBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState("pre");
  const [newLabel, setNewLabel] = useState("");
  const [newBody, setNewBody] = useState("");
  const [adding, setAdding] = useState(false);

  const reload = useCallback(async () => {
    if (!bundle?.category_id) { setLoading(false); return; }
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/category-confirmation-blocks?category_id=${encodeURIComponent(bundle.category_id)}&event_id=${encodeURIComponent(eventId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) { const d = await r.json(); setHostBlocks(d.host_blocks || []); }
    } catch {} finally { setLoading(false); }
  }, [API, bundle?.category_id, eventId]);

  useEffect(() => { reload(); }, [reload]);

  const addBlock = async () => {
    if (!newLabel.trim()) { toast.error("Enter a label"); return; }
    setAdding(true);
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/category-confirmation-blocks`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ category_id: bundle.category_id, event_id: eventId, stage: activeStage, label: newLabel, body: newBody })
      });
      if (r.ok) { toast.success(`Added to ${activeStage}`); setNewLabel(""); setNewBody(""); reload(); }
      else toast.error("Could not add");
    } catch { toast.error("Network error"); } finally { setAdding(false); }
  };

  const removeBlock = async (bid) => {
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/category-confirmation-blocks/${bid}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { toast.success("Removed"); reload(); }
    } catch {}
  };

  const stages = [{ key: "pre", label: "Pre-appointment" }, { key: "during", label: "During" }, { key: "post", label: "Post-appointment" }];
  const adminBlocks = (bundle?.confirmation_blocks?.[activeStage] || []).map(b => ({ ...b, stage: activeStage, scope: "admin" }));
  const filteredHost = hostBlocks.filter(b => b.stage === activeStage);

  return (
    <CardContent className="pt-0 pb-4 border-t">
      <div className="pt-4 space-y-4">
        <div className="space-y-2">
          {[
            { key: "show_pricing", label: "Show pricing", icon: DollarSign },
            { key: "show_estimates", label: "Show duration / estimates", icon: DollarSign },
            { key: "show_location_full", label: "Reveal full location post-confirmation only", icon: MapPin },
            { key: "show_private_inspo", label: "Allow private inspo upload (client + host curated)", icon: Lock },
          ].map((tog) => {
            const Icon = tog.icon;
            const fp = `confirmation_${tog.key}`;
            const value = settings[fp] !== undefined ? !!settings[fp] : true;
            return (
              <div key={tog.key} className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Icon className="w-3.5 h-3.5 text-gray-400" /><span className="text-sm text-gray-700">{tog.label}</span></div>
                <div className="flex items-center gap-2">
                  {saving === fp && <Loader2 className="w-3 h-3 animate-spin text-purple-500" />}
                  <Switch checked={value} onCheckedChange={(v) => onToggle("confirmation", tog.key, v)} data-testid={`toggle-${fp}`} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-purple-50 border border-purple-100 rounded-lg p-3 text-[11px] text-purple-800 flex items-start gap-2">
          <Lock className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span><strong>Private inspo</strong> - clients upload private images on this page post-booking, AND you can curate per-client picks from your private gallery. Both surface inside the Clients tab Preference Card drawer.</span>
        </div>

        <div className="border-t pt-3">
          <div className="flex items-center gap-2 mb-3">
            {stages.map(s => (
              <button key={s.key} onClick={() => setActiveStage(s.key)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${activeStage === s.key ? "bg-purple-600 text-white border-purple-600" : "bg-white text-gray-700 border-gray-200 hover:border-purple-300"}`} data-testid={`confirmation-stage-${s.key}`}>{s.label}</button>
            ))}
          </div>
          {loading ? <div className="text-center py-3 text-xs text-gray-400">Loading...</div> : (
            <div className="space-y-2">
              {adminBlocks.length === 0 && filteredHost.length === 0 && <p className="text-xs text-gray-400 italic">No content for this stage yet. Add the first block below.</p>}
              {adminBlocks.map((b) => (
                <div key={b.block_id} className="p-2.5 rounded-lg border border-gray-100 bg-gray-50/50" data-testid={`admin-block-${b.block_id}`}>
                  <div className="flex items-start gap-2">
                    <Lock className="w-3 h-3 text-gray-400 mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-800">{b.label}</p><p className="text-xs text-gray-600 mt-0.5">{b.body}</p></div>
                    <Badge variant="outline" className="text-[10px]">Admin</Badge>
                  </div>
                </div>
              ))}
              {filteredHost.map((b) => (
                <div key={b.block_id} className="p-2.5 rounded-lg border border-purple-100 bg-purple-50/40" data-testid={`host-block-${b.block_id}`}>
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-3 h-3 text-purple-500 mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-800">{b.label}</p><p className="text-xs text-gray-600 mt-0.5">{b.body}</p></div>
                    <Button size="sm" variant="ghost" onClick={() => removeBlock(b.block_id)} data-testid={`remove-block-${b.block_id}`}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                  </div>
                </div>
              ))}
              <div className="border-t pt-3 mt-3 space-y-2">
                <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Title (e.g. 'Day-of arrival')" className="text-sm" data-testid="confirmation-add-label" />
                <textarea value={newBody} onChange={(e) => setNewBody(e.target.value)} placeholder="Content shown to the client..." className="w-full text-sm border rounded px-3 py-2 min-h-[60px]" data-testid="confirmation-add-body" />
                <Button size="sm" onClick={addBlock} disabled={adding} data-testid="confirmation-add-btn">
                  {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />} Add to {activeStage}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </CardContent>
  );
}

// =============== MAIN PANEL ===============
export default function ClientExperiencePanel({ API, eventId, event, onSaved, onJumpToBrand, onJumpToMedia, onJumpToServices }) {
  const [settings, setSettings] = useState(event?.settings || {});
  const [openStages, setOpenStages] = useState({ envelope: true, intake: true, confirmation: true });
  const [saving, setSaving] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [loadingBundle, setLoadingBundle] = useState(true);

  const categoryId = (event?.pilot_category || event?.event_type?.replace?.('_pilot', '') || '').toLowerCase();

  useEffect(() => { setSettings(event?.settings || {}); }, [event?.settings]);

  const loadBundle = useCallback(async () => {
    if (!eventId || !categoryId) { setLoadingBundle(false); return; }
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/host/category-bundle?category_id=${encodeURIComponent(categoryId)}&event_id=${encodeURIComponent(eventId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) setBundle(await r.json());
    } catch (e) { console.warn("Could not load category bundle", e); } finally { setLoadingBundle(false); }
  }, [API, eventId, categoryId]);

  useEffect(() => { loadBundle(); }, [loadBundle]);

  const setToggle = async (stageKey, toggleKey, value) => {
    const fp = `${stageKey}_${toggleKey}`;
    setSaving(fp);
    setSettings({ ...settings, [fp]: value });
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/events/${eventId}/settings`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ [fp]: value }),
      });
      if (!r.ok) throw new Error();
      toast.success("Saved");
      onSaved && onSaved();
    } catch {
      toast.error("Could not save");
      setSettings(settings);
    } finally { setSaving(null); }
  };

  return (
    <div className="space-y-6" data-testid="client-experience-panel">
      <div>
        <h3 className="text-lg font-semibold">Client Experience</h3>
        <p className="text-sm text-gray-500">Control what clients see at every stage. All sections sync from the admin Category Master so there's no duplicate data entry.</p>
      </div>

      {bundle && (
        <Card className="bg-purple-50 border-purple-200" data-testid="sync-config-card-client-exp">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><Layers className="w-4 h-4 text-purple-700" /><h4 className="text-sm font-semibold">Pulled from admin Category Master · {bundle.label}</h4></div>
            <p className="text-xs text-gray-500 mb-3">Preferences, inspo folders, master products and confirmation blocks are managed by admin and shared across all {bundle.label} hosts. You toggle visibility and add your own touches below.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="bg-white/70 rounded-lg p-2 text-center"><p className="text-xl font-bold text-purple-700">{(bundle.preference_schema || []).reduce((n, b) => n + (b.fields?.length || 0), 0)}</p><p className="text-[10px] text-gray-600">Preference fields</p></div>
              <div className="bg-white/70 rounded-lg p-2 text-center"><p className="text-xl font-bold text-purple-700">{(bundle.inspo_folders || []).length}</p><p className="text-[10px] text-gray-600">Inspo folders</p></div>
              <div className="bg-white/70 rounded-lg p-2 text-center"><p className="text-xl font-bold text-purple-700">{(bundle.master_products || []).length}</p><p className="text-[10px] text-gray-600">Master products</p></div>
              <div className="bg-white/70 rounded-lg p-2 text-center"><p className="text-xl font-bold text-purple-700">{(bundle.host_private_inspo_count || 0)}</p><p className="text-[10px] text-gray-600">Your private inspo</p></div>
            </div>
            <p className="text-[10px] text-gray-400 mt-3">Edit defaults in <code>/admin/category-master</code>.</p>
          </CardContent>
        </Card>
      )}

      {loadingBundle && <div className="text-center py-3 text-xs text-gray-400 flex items-center justify-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading category master...</div>}

      {STAGE_META.map((stage) => {
        const Icon = stage.icon;
        const isOpen = openStages[stage.key];
        return (
          <Card key={stage.key} data-testid={`stage-${stage.key}`}>
            <button onClick={() => setOpenStages({ ...openStages, [stage.key]: !isOpen })} className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors" data-testid={`stage-${stage.key}-toggle`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center"><Icon className="w-4 h-4 text-purple-700" /></div>
                <div className="text-left"><h4 className="font-semibold text-sm">{stage.title}</h4><p className="text-xs text-gray-500">{stage.description}</p></div>
              </div>
              {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
            {isOpen && stage.key === "envelope" && <EnvelopeStage event={event} settings={settings} onToggle={setToggle} onJumpToBrand={onJumpToBrand} saving={saving} />}
            {isOpen && stage.key === "intake" && <IntakeStage API={API} eventId={eventId} bundle={bundle} settings={settings} onToggle={setToggle} saving={saving} onOpenMedia={onJumpToMedia} onOpenServices={onJumpToServices} refresh={loadBundle} />}
            {isOpen && stage.key === "confirmation" && <ConfirmationStage API={API} eventId={eventId} bundle={bundle} settings={settings} onToggle={setToggle} saving={saving} />}
          </Card>
        );
      })}

      <Card className="bg-gray-50">
        <CardContent className="p-4 flex items-start gap-3">
          <Eye className="w-4 h-4 text-gray-500 mt-0.5" />
          <div className="text-xs text-gray-600">
            <p className="font-medium mb-1">All stages are admin-first and synced.</p>
            <p>These settings power the public RSVP / invite envelope, the preference share page <code>/c/{`{card_id}`}</code>, and the host appointment card. Toggle anything off to hide it from the client view.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
